import { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Download, FileText, Image, Route, Loader2, Package } from "lucide-react";

/**
 * ExportDialog - Export dialog for trips
 * 
 * For editors (can_edit): Shows checkboxes to select what to include (PDF, photos, routes)
 * For visitors: Shows simple confirmation (PDF + routes, no photos)
 */
export function ExportDialog({ open, onOpenChange, onExport, canEdit, exporting }) {
  const { t } = useTranslation("common");
  const [includePdf, setIncludePdf] = useState(true);
  const [includePhotos, setIncludePhotos] = useState(true);
  const [includeRoutes, setIncludeRoutes] = useState(true);

  const handleExport = () => {
    if (canEdit) {
      onExport({ includePdf, includePhotos, includeRoutes });
    } else {
      onExport({ includePdf: true, includePhotos: false, includeRoutes: true });
    }
  };

  const nothingSelected = canEdit && !includePdf && !includePhotos && !includeRoutes;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="export-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Download className="h-5 w-5 text-primary" />
            {t("trip_detail.export")}
          </DialogTitle>
          <DialogDescription>
            {canEdit
              ? t("export_dialog.editor_description", "Escolhe o que queres incluir na exportação. Receberás os ficheiros por email.")
              : t("export_dialog.visitor_description", "Receberás o PDF e os ficheiros de rota por email.")
            }
          </DialogDescription>
        </DialogHeader>

        {canEdit ? (
          <div className="space-y-4 py-2" data-testid="export-options">
            <div className="flex items-center space-x-3" data-testid="export-option-pdf">
              <Checkbox
                id="export-pdf"
                checked={includePdf}
                onCheckedChange={setIncludePdf}
                data-testid="export-checkbox-pdf"
              />
              <Label htmlFor="export-pdf" className="flex items-center gap-2 cursor-pointer">
                <FileText className="h-4 w-4 text-red-500" />
                <div>
                  <span className="font-medium">PDF</span>
                  <p className="text-xs text-muted-foreground">{t("export_dialog.pdf_desc", "Documento PDF com layout profissional")}</p>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-3" data-testid="export-option-photos">
              <Checkbox
                id="export-photos"
                checked={includePhotos}
                onCheckedChange={setIncludePhotos}
                data-testid="export-checkbox-photos"
              />
              <Label htmlFor="export-photos" className="flex items-center gap-2 cursor-pointer">
                <Image className="h-4 w-4 text-green-500" />
                <div>
                  <span className="font-medium">{t("export_dialog.photos_label", "Fotos originais")}</span>
                  <p className="text-xs text-muted-foreground">{t("export_dialog.photos_desc", "Todas as fotos em resolução original")}</p>
                </div>
              </Label>
            </div>
            <div className="flex items-center space-x-3" data-testid="export-option-routes">
              <Checkbox
                id="export-routes"
                checked={includeRoutes}
                onCheckedChange={setIncludeRoutes}
                data-testid="export-checkbox-routes"
              />
              <Label htmlFor="export-routes" className="flex items-center gap-2 cursor-pointer">
                <Route className="h-4 w-4 text-blue-500" />
                <div>
                  <span className="font-medium">{t("export_dialog.routes_label", "Rotas (GPX)")}</span>
                  <p className="text-xs text-muted-foreground">{t("export_dialog.routes_desc", "Ficheiros de rota para GPS ou outras apps")}</p>
                </div>
              </Label>
            </div>
          </div>
        ) : (
          <div className="py-2 space-y-2 text-sm text-muted-foreground" data-testid="export-visitor-info">
            <div className="flex items-center gap-2">
              <FileText className="h-4 w-4 text-red-500" />
              <span>PDF</span>
            </div>
            <div className="flex items-center gap-2">
              <Route className="h-4 w-4 text-blue-500" />
              <span>{t("export_dialog.routes_label", "Rotas (GPX)")}</span>
            </div>
          </div>
        )}

        <DialogFooter className="gap-2 sm:gap-0">
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={exporting}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleExport}
            disabled={exporting || nothingSelected}
            data-testid="export-confirm-btn"
          >
            {exporting ? (
              <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Package className="h-4 w-4 mr-2" />
            )}
            {exporting ? t("trip_detail.exporting") : t("trip_detail.export")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
